import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    role: 'user',
    subscription: {
      isActive: false,
      plan: '',
      startDate: '',
      endDate: ''
    }
  });

  // Search and pagination states
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
    } else {
      const lowercaseSearch = searchTerm.toLowerCase();
      const filtered = users.filter(user => 
        user.username?.toLowerCase().includes(lowercaseSearch) ||
        user.email?.toLowerCase().includes(lowercaseSearch) ||
        user.role?.toLowerCase().includes(lowercaseSearch) ||
        user.subscription?.plan?.toLowerCase().includes(lowercaseSearch)
      );
      setFilteredUsers(filtered);
    }
    setCurrentPage(1);
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/api/admin/users');
      setUsers(res.data);
      setFilteredUsers(res.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Get current users for pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username || '',
      email: user.email || '',
      role: user.role || 'user',
      subscription: {
        isActive: user.subscription?.isActive || false,
        plan: user.subscription?.plan || '',
        startDate: user.subscription?.startDate ? new Date(user.subscription.startDate).toISOString().split('T')[0] : '',
        endDate: user.subscription?.endDate ? new Date(user.subscription.endDate).toISOString().split('T')[0] : ''
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/api/admin/users/${editingUser._id}`, formData);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Échec de la mise à jour de l\'utilisateur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;

    try {
      await axios.delete(`/api/admin/users/${id}`);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Échec de la suppression de l\'utilisateur');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-white mb-8">Gérer les Utilisateurs</h1>

        {editingUser && (
          <div className="bg-gray-800 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-6">Modifier l'Utilisateur</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white mb-2">Nom d'utilisateur</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-white mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white mb-2">Rôle</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                >
                  <option value="user">Utilisateur</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-white font-semibold mb-4">Abonnement</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.subscription.isActive}
                        onChange={(e) => setFormData({
                          ...formData,
                          subscription: { ...formData.subscription, isActive: e.target.checked }
                        })}
                        className="w-4 h-4"
                      />
                      <span className="text-white">Abonnement Actif</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-white mb-2">Plan</label>
                    <select
                      value={formData.subscription.plan}
                      onChange={(e) => setFormData({
                        ...formData,
                        subscription: { ...formData.subscription, plan: e.target.value }
                      })}
                      className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                    >
                      <option value="">Aucun</option>
                      <option value="1month">1 Mois</option>
                      <option value="6months">6 Mois</option>
                      <option value="12months">12 Mois</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white mb-2">Date de début</label>
                    <input
                      type="date"
                      value={formData.subscription.startDate}
                      onChange={(e) => setFormData({
                        ...formData,
                        subscription: { ...formData.subscription, startDate: e.target.value }
                      })}
                      className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-white mb-2">Date de fin</label>
                    <input
                      type="date"
                      value={formData.subscription.endDate}
                      onChange={(e) => setFormData({
                        ...formData,
                        subscription: { ...formData.subscription, endDate: e.target.value }
                      })}
                      className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                    />
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  className="px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition font-semibold"
                >
                  Mettre à jour l'Utilisateur
                </button>
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-gray-800 rounded-lg overflow-hidden">
          {/* Search Bar */}
          <div className="px-6 py-4 border-b border-gray-700">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Rechercher des utilisateurs par nom d'utilisateur, email, rôle ou plan..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="text-gray-400 text-sm">
                {filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="px-6 py-4 text-left text-white">Nom d'utilisateur</th>
                  <th className="px-6 py-4 text-left text-white">Email</th>
                  <th className="px-6 py-4 text-left text-white">Rôle</th>
                  <th className="px-6 py-4 text-left text-white">Abonnement</th>
                  <th className="px-6 py-4 text-left text-white">Actions</th>
                </tr>
              </thead>
              <tbody key={currentUsers.length === 0 ? 'empty' : 'populated'}>
                {currentUsers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-400">
                      {searchTerm ? 'Aucun utilisateur trouvé correspondant à votre recherche.' : 'Aucun utilisateur disponible.'}
                    </td>
                  </tr>
                ) : (
                  currentUsers.map((user) => (
                    <tr key={user._id} className="border-b border-gray-700">
                      <td className="px-6 py-4 text-white">{user.username}</td>
                      <td className="px-6 py-4 text-gray-300">{user.email}</td>
                      <td className="px-6 py-4 text-gray-300 capitalize">{user.role === 'user' ? 'Utilisateur' : 'Admin'}</td>
                      <td className="px-6 py-4 text-gray-300">
                        {user.subscription?.isActive && user.subscription?.endDate && new Date(user.subscription.endDate) > new Date() ? (
                          <span className="text-green-400">
                            {user.subscription.plan} jusqu'au {formatDate(user.subscription.endDate)}
                          </span>
                        ) : (
                          <span className="text-red-400">Aucun abonnement actif</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(user)}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDelete(user._id)}
                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
              <div className="text-gray-400 text-sm">
                Affichage de {indexOfFirstUser + 1} à {Math.min(indexOfLastUser, filteredUsers.length)} sur {filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? 's' : ''}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Précédent
                </button>
                
                <div className="flex space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => paginate(pageNum)}
                        className={`px-4 py-2 rounded-lg transition ${
                          currentPage === pageNum
                            ? 'bg-white text-gray-900 font-semibold'
                            : 'bg-gray-700 text-white hover:bg-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;
